<template>
    <mobile-panel-wrapper
        :is-open="isOpen" element-height="250px"
        class="items-center" v-if="mode === 'list'">
        <div class="flex space-x-6 mb-8 mt-8">
            <placement-button @click="listAddText" :text="$t('TEXT_ADD')" v-if="!isGroupOrder">
                <close-button class="rotate-45" />
            </placement-button>

            <placement-button
                v-for="text in filledTextPlacements" :key="text.placement.code"
                @click="listEditText(text)" :text="text.component.custom['personalization-text']">
                <img src="../../../assets/text.svg" height="26" width="26">
            </placement-button>
        </div>
        <div class="flex flex-col flex-1"></div>
        <action-buttons-wrapper>
            <secondary-button @click="saveEdit">
                {{ $t('MENU_BACK') }}
            </secondary-button>
        </action-buttons-wrapper>
    </mobile-panel-wrapper>
    <mobile-panel-wrapper
        :is-open="isOpen" :element-height="elementHeight"
        class="items-center flex flex-col" v-else-if="mode === 'edit'">
        <!-- Input -->
        <div
            :class="['flex flex-col items-center w-full transition-all ease-in-out duration-300 overflow-hidden max-w-md mx-auto',
                     subScreen === 'text-input' ? 'h-24' : 'h-0'
            ]">
            <input
                class="w-full p-2 border border-stone-400 text-[16px] mt-8" placeholder="Enter text"
                v-model.trim="selectedText" @input="updateText"
            />
        </div>
        <div class="w-screen">
            <!-- Fonts -->
            <div
                :class="['flex flex-col items-center transition-all ease-in-out duration-300 overflow-y-hidden px-4',
                         subScreen === 'font' ? 'h-24' : 'h-0' ]">
                <horizontal-scroll-outer-wrapper>
                    <horizontal-scroll-inner-wrapper class="space-x-6 py-1">
                        <div
                            v-for="font in availableFonts" :key="font.name"
                            class="cursor-pointer mt-6"
                            :class="[selectedFont.name === font.name ? 'border border-stone-900' : 'border border-transparent']"
                            @click.prevent="selectedFont = font">
                            <div
                                class="text-2xl w-12 h-8" :style="getFontStyle(font)"
                                :class="[
                                    // Fonts have different heights, so we need to adjust the margin-top
                                    font.name === 'Sacramento'
                                        || font.name === 'Charmonman'
                                        || font.name.includes('Luckiest Guy')
                                        || font.name.includes('Great Vibes')
                                        ? 'mt-3'
                                        : 'mt-2'
                                ]"
                            >Aa</div>
                        </div>
                    </horizontal-scroll-inner-wrapper>
                </horizontal-scroll-outer-wrapper>
            </div>
        </div>
        <!-- colors -->
        <div
            :class="['flex flex-col items-center w-full transition-all ease-in-out duration-300 overflow-hidden',
                     subScreen === 'color' ? 'h-24' : 'h-0' ]">
        </div>
        <!-- Alignment -->
        <div
            :class="['flex flex-col items-center w-full transition-all ease-in-out duration-300 overflow-hidden',
                     subScreen === 'alignment' ? 'h-24' : 'h-0' ]">

            <div class="flex mt-6">
                <button
                    class="cz2__image-button cz2__image-button--align-left"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_LEFT')" @click.prevent="alignLeft">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--align-center cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_CENTER')" @click.prevent="alignCenter">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--align-right cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_RIGHT')" @click.prevent="alignRight">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--align-top cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_TOP')" @click.prevent="alignTop">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--align-v-center cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_VCENTER')" @click.prevent="alignVCenter">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--align-bottom cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_ALIGN_BOTTOM')" @click.prevent="alignBottom">
                </button>
            </div>
        </div>
        <!-- Layering -->
        <div
            v-if="isPrintingMode"
            :class="['flex flex-col items-center justify-center w-full transition-all ease-in-out duration-300 overflow-hidden',
                     subScreen === 'layering' ? 'h-24' : 'h-0' ]">
            <div class="flex -mt-2">
                <button
                    class="cz2__image-button cz2__image-button--layer-down"
                    :aria-label="$t('IMAGE_EDIT_LAYER_DOWN')" @click.prevent="layerDown">
                </button>

                <button
                    class="cz2__image-button cz2__image-button--layer-up cz2__ml-10"
                    :aria-label="$t('IMAGE_EDIT_LAYER_UP')" @click.prevent="layerUp">
                </button>
            </div>
        </div>
        <div class="flex flex-col flex-1"></div>
        <div class="w-full border-b border-gray-300 my-2" />
        <div class="flex flex-col max-w-full px-4">
            <div class="flex pb-2 overflow-x-auto max-w-full my-1 space-x-2">
                <secondary-button-short
                    :is-active="subScreen === 'text-input'"
                    @click="setSubScreen('text-input')">
                    Text
                </secondary-button-short>
                <secondary-button-short
                    :is-active="subScreen === 'font'"
                    @click="setSubScreen('font')">
                    Fonts
                </secondary-button-short>
                <secondary-button-short
                    :is-active="subScreen === 'alignment'"
                    @click="setSubScreen('alignment')">
                    Align
                </secondary-button-short>
                <secondary-button-short
                    v-if="isPrintingMode"
                    :is-active="subScreen === 'layering'"
                    @click="setSubScreen('layering')">
                    Layers
                </secondary-button-short>
                <secondary-button-short
                    class="cursor-pointer border border-stone-400 p-1 px-4 mr-4 font-bold uppercase"
                    @click="removeText">
                    Remove
                </secondary-button-short>
            </div>
        </div>
        <action-buttons-wrapper>
            <secondary-button @click="cancel">
                {{ $t('ACTION_CANCEL') }}
            </secondary-button>
            <primary-button @click="selectedText && saveEdit()" :disabled="!selectedText">
                {{ $t('ACTION_SAVE') }}
            </primary-button>
        </action-buttons-wrapper>
    </mobile-panel-wrapper>
</template>

<script>
    import Multiselect from 'vue-multiselect';
    import { mapActions, mapState } from 'vuex';
    import MobilePanelWrapper from './MobilePanelWrapper.vue';
    import PanelsWrapper from '../PanelsWrapper.vue';
    import designArea from '../../../utilities/designArea';
    import * as constants from '../../../constants';
    import extent from '../../../utilities/extent';
    import HorizontalScrollWrapper from '../../HorizontalScrollWrapper.vue';
    import ActionButtonsWrapper from '../../buttons/ActionButtonsWrapper.vue';
    import PrimaryButton from '../../buttons/PrimaryButton.vue';
    import SecondaryButton from '../../buttons/SecondaryButton.vue';
    import SecondaryButtonShort from '../../buttons/SecondaryButtonShort.vue';
    import PlacementButton from '../../buttons/PlacementButton.vue';
    import CloseButton from '../../buttons/CloseButton.vue';
    import HorizontalScrollInnerWrapper from '../../HorizontalScrollInnerWrapper.vue';
    import HorizontalScrollOuterWrapper from '../../HorizontalScrollOuterWrapper.vue';

    import * as Tours from '../../../tours';
    import { fillEmptyDivs } from '../../../utils';

    export default {
        components: {
            HorizontalScrollInnerWrapper,
            HorizontalScrollOuterWrapper,
            CloseButton,
            PlacementButton,
            MobilePanelWrapper,
            PanelsWrapper,
            Multiselect,
            HorizontalScrollWrapper,
            ActionButtonsWrapper,
            PrimaryButton,
            SecondaryButton,
            SecondaryButtonShort,
        },
        data() {
            return {
                group: 'individual',
                subScreen: 'text-input',
                textLockDisabled: true,

                mode: 'list',
                editMode: null,

                editPlacement: null,
                editComponent: null,

                selectedText: '',
                selectedFont: '',

                lastSelectedNonprimary: null,
            };
        },
        props: {
            isOpen: {
                type: Boolean,
                default: false,
            },
            elementHeight: {
                type: String,
                required: true,
            },
        },
        computed: {
            ...mapState({
                selectedStep: (state) => state.selectedStep,
            }),
            isPrintingMode() {
                return this.viewController.blueprint.custom['v-personalzation-type'] === 'printing';
            },
            /**
             * Current view controller.
             */
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            /**
             * Currently selected view.
             */
            currentView() {
                return this.$store.state.customizer.currentView;
            },

            /**
             * Gets a list of filled images.
             */
            filledTextPlacements() {
                return this.$store.getters.filledTextPlacements;
            },

            /**
             * Gets a list of free images.
             */
            emptyTextPlacements() {
                return this.$store.getters.emptyTextPlacements;
            },

            /**
             * Current customization mode.
             */
            personalizationMethod() {
                return this.$store.state.customizer.personalizationMethod;
            },

            /**
             * Figure out a list of available fonts.
             */
            availableFonts() {
                if (this.editPlacement) {
                    const fonts = [];

                    this.editPlacement.components.forEach((c) => {
                        const font = c.description.find((d) => d.definition === constants.DEFINITION_FONT);

                        if (font) {
                            if (!fonts.some((f) => f.code === font.code)) {
                                const copy = { ...font };

                                if (copy.custom == null) {
                                    copy.custom = {};
                                }

                                copy.custom = { ...copy.custom, ...c.custom };

                                fonts.push(copy);
                            }
                        }
                    });

                    return fonts;
                }

                return [];
            },

            /**
             * Figure out a list of available colors.
             */
            availableColors() {
                return this.$store.getters.availablePersonalizationColors;
            },

            /**
             * Get a list of primary colors to be shown in the plain list.
             */
            primaryColors() {
                const primaries = [];

                if (this.lastSelectedNonprimary) {
                    primaries.push(this.lastSelectedNonprimary);
                }

                primaries.push(...this.availableColors.filter((c) => {
                    if (c.custom && c.custom['v-primary-color'] === 'true') {
                        return true;
                    }

                    return false;
                }));

                return primaries;
            },

            /**
             * Figure out the selected color caption.
             */
            selectedColorText() {
                const color = this.selectedColor;

                return color ? color.name : '';
            },

            /**
             * Get selected color.
             */
            selectedColor() {
                if (this.editPlacement) {
                    const selectedActual = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    const selected = this.availableColors.find((x) => x.custom.hex === selectedActual.custom['personalization-color']);

                    return selected;
                }

                return null;
            },

            /**
             * Figure out the selected color CSS style.
             */
            selectedColorStyle() {
                const css = {};

                if (this.editPlacement) {
                    const selected = this.viewController.selectedAtPlacement(this.editPlacement.code);

                    const hex = selected.custom['personalization-color'];

                    css.backgroundColor = hex.startsWith('#') ? hex : `#${hex}`;
                }

                return css;
            },

            /**
             * Is the current order for a group
             */
            isGroupOrder() {
                return this.$store.state.isGroupOrder;
            },

            /**
             * Header for the panel
             */
            panelHeader() {
                const { isGroupOrder } = this;
                if (isGroupOrder) {
                    return this.$t('TEXT_STYLES');
                }
                return this.$t('TEXT_ADD_OR_EDIT');
            },

            /**
             * Secondary bottles array.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles;
            },
        },
        watch: {
            selectedFont() {
                this.updateFont();
            },

            /**
             * Load font files so we could show the fonts preview.
             */
            availableFonts() {
                this.$store.dispatch('loadFonts', this.availableFonts);
            },

            /**
             * Saves the last selected non primary color so it could be shown in the quick picker.
             */
            selectedColor() {
                if (this.editPlacement && this.selectedColor) {
                    if (this.selectedColor.custom['v-primary-color'] !== 'true') {
                        this.lastSelectedNonprimary = this.selectedColor;
                    }
                }
            },

            /**
             * Move the design if current view changes.
             */
            currentView() {
                this.moveDesign();
            },
        },
        mounted() {
            // Otherwise this messes with transition animation because browsers will try to scroll to the focused item, which would be off-screen.
            this.$store.state.eventBus.$once('secondaryEntered', () => {
                this.textLockDisabled = false;
            });

            // When the component is loaded, capture the current state of the view controller and save it in case
            // we need to revert to the previous state and toss away all customizations.
            //
            // At any given time there should be only one module that can do the revert.
            this.captureController();

            if (this.filledTextPlacements.length === 0) {
                if (this.emptyTextPlacements.length > 0) {
                    this.listAddText();
                }
            }

            if (this.selectedColor && this.selectedColor.custom && this.selectedColor.custom['v-primary-color'] !== 'true') {
                this.lastSelectedNonprimary = this.selectedColor;
            }
            fillEmptyDivs();
        },
        beforeDestroy() {
            // Reenable swiping.
            this.$store.dispatch('lockSwipe', false);
            this.$store.dispatch('editPlacement', null);
            this.editPlacement = null;
        },
        methods: {
            ...mapActions({
                selectStep: 'selectStep',
            }),
            goToMainScreen() {
                this.selectStep(null);
            },
            setSubScreen(subScreen) {
                this.subScreen = subScreen;
            },
            captureController() {
                if (this.viewController) {
                    this.revertController = this.viewController.clone({ excludeEvents: false });
                    this.revertBottles = JSON.parse(JSON.stringify(this.secondaryBottles));
                }
            },

            /**
             * Accept color choice.
             */
            accept() {
                // Keep the current configured view controller, and go back to the main screen.
                this.$store.dispatch('selectStep', null);
            },

            /**
             * Reject color changes and restore the original selection.
             */
            cancel() {
                this.$store.dispatch('showGenericConfirm', {
                    title: this.$t('CANCEL_CONFIRMATION'),
                    description: this.$t('CANCEL_CONFIRMATION_BODY'),
                    cancelAction: this.$t('CANCEL_CONFIRMATION_CANCEL'),
                    confirmAction: this.$t('CANCEL_CONFIRMATION_OK'),
                    confirmationCallback: () => {
                        // Revert back to the state of the controller as was captured when the component was mounted.
                        this.$store.dispatch('showController', this.revertController);
                        this.$store.dispatch('setSecondaryBottles', this.revertBottles);

                        // Go back to the main screen.
                        this.$store.dispatch('selectStep', null);
                    },
                });
            },

            /**
             * Enters text editing mode.
             */
            beginEdit(placement, component) {
                this.undoEditController = this.viewController.clone({ excludeEvents: false });
                this.undoBottles = JSON.parse(JSON.stringify(this.secondaryBottles));

                this.editPlacement = placement;
                this.editComponent = component;
                this.mode = 'edit';

                this.$store.dispatch('lockSwipe', true);
                this.$store.dispatch('editPlacement', placement);

                this.selectedText = component.custom['personalization-text'] || '';

                const font = component.description.find((d) => d.definition === constants.DEFINITION_FONT);
                if (font) {
                    this.selectedFont = { ...font, ...{ custom: { ...font.custom, ...component.custom } } };
                }

                if (component.custom['v-design-on']) {
                    this.$store.dispatch('switchToDesignView', component.custom['v-design-on']);
                }

                setTimeout(() => {
                    this.$store.dispatch('setCurrentTour', Tours.editSmall());
                }, 250);
            },

            /**
             * Cancels image editing. Return to main menu.
             */
            cancelEdit() {
                // This was the only placement being created, do a full rollback.
                if (this.filledTextPlacements.length === 1) {
                    this.cancel();
                    return;
                }

                if (this.undoEditController) {
                    this.$store.dispatch('showController', this.undoEditController);
                    this.$store.dispatch('setSecondaryBottles', this.undoBottles);
                    this.undoEditController = null;
                    this.undoBottles = null;
                }

                this.mode = 'list';

                this.$store.dispatch('lockSwipe', false);
                this.$store.dispatch('editPlacement', null);
                this.editPlacement = null;
                // this.$store.dispatch('selectStep', null);
            },

            /**
             * Saves image edits. Return to main menu.
             */
            saveEdit() {
                if (this.mode === 'list') {
                    this.$store.dispatch('selectStep', null);
                } else {
                    this.mode = 'list';
                    this.$store.dispatch('lockSwipe', false);
                    this.$store.dispatch('editPlacement', null);
                    this.editPlacement = null;
                }
            },

            /**
             * Update font selection.
             */
            updateFont() {
                this.viewController.updateSimilarComponent(this.editPlacement.code, constants.DEFINITION_FONT, this.selectedFont.code);
            },

            /**
             * Update personalization.
             */
            updateText() {
                this.viewController.updatePersonalization(this.editPlacement.code, this.selectedText).then((result) => {
                    if (result.accepted) {
                        if (result.as) {
                            this.selectedText = result.as;
                        }
                    } else if (result.lastValid != null) {
                        this.selectedText = result.lastValid;
                    }
                });
            },

            /**
             * Clear text personalization.
             */
            clearText() {
                this.selectedText = '';

                this.viewController.updatePersonalization(this.editPlacement.code, '').then((result) => {
                    if (result.accepted) {
                        if (result.as) {
                            this.selectedText = result.as;
                        }
                    } else if (result.lastValid != null) {
                        this.selectedText = result.lastValid;
                    }
                });
            },

            /**
             * Change text.
             */
            changeText() {
                this.editMode = 'text';
            },

            /**
             * Align the image to the left.
             */
            alignLeft() {
                if (this.currentView) {
                    const { left } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (left != null) {
                        const { dx, w } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-x': left + dx - w,
                            'personalization-alignment': 'left',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Align the image to the center.
             */
            alignCenter() {
                if (this.currentView) {
                    const { left, right } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (left != null && right != null) {
                        const { w } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-x': ((left + right) * 0.5) - w,
                            'personalization-alignment': 'center',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Align the image to the right.
             */
            alignRight() {
                if (this.currentView) {
                    const { right } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (right != null) {
                        const { dx, w } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-x': right - dx - w,
                            'personalization-alignment': 'right',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Align the image to the top.
             */
            alignTop() {
                if (this.currentView) {
                    const { top } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (top != null) {
                        const { dy, h } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-y': top + dy - h,
                            'personalization-vertical-alignment': 'top',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Align the image to the center.
             */
            alignVCenter() {
                if (this.currentView) {
                    const { top, bottom } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (top != null && bottom != null) {
                        // eslint-disable-next-line
                    const { h, descent, ascent, spacing } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-y': ((top + bottom) * 0.5) - (h * 0.5) - descent - (spacing + ascent),
                            'personalization-vertical-alignment': 'center',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Align the image to the bottom.
             */
            alignBottom() {
                if (this.currentView) {
                    const { bottom } = designArea(this.$store.state.customizer, this.editComponent.custom['v-design-on']);

                    if (bottom != null) {
                        const { dy, h, descent } = extent(this.editPlacement, this.editComponent);

                        this.viewController.commitCustomAttributes(this.editPlacement.code, {
                            'personalization-y': bottom - dy - h - descent,
                            'personalization-vertical-alignment': 'bottom',
                        }, {
                            propagate: true,
                        });
                    }
                }
            },

            /**
             * Move the current image up a layer.
             */
            layerUp() {
                this.$store.dispatch('layerUp', this.editPlacement);
            },

            /**
             * Move the current image down a layer.
             */
            layerDown() {
                this.$store.dispatch('layerDown', this.editPlacement);
            },

            /**
             * Begins adding a new text.
             */
            listAddText() {
                if (this.emptyTextPlacements.length > 0) {
                    this.$store.dispatch('validateEditableViews');
                    this.$store.dispatch('switchToCommonView');

                    const { placement, component } = this.emptyTextPlacements[0];

                    this.$store.dispatch('takeEmptyPlacement', placement);

                    // ... and center manually in the current view.
                    if (this.currentView) {
                        const { view } = this.currentView;

                        this.$store.dispatch('renderOnlyOnView', {
                            view: this.currentView,
                        });

                        if (view.custom) {
                            const {
                                left, right, top, bottom,
                            } = designArea(this.$store.state.customizer, view.custom['v-design-area']);

                            const width = +component.custom['personalization-width'];
                            const height = +component.custom['personalization-height'];

                            if (left != null && right != null && top != null && bottom != null) {
                                let yOffset = null;

                                let fontScale = null;
                                let fontColor = null;
                                let fontFamily = null;

                                this.filledTextPlacements.forEach((p) => {
                                    if (p.component.custom['personalization-y'] != null) {
                                        if (yOffset == null) {
                                            yOffset = +p.component.custom['personalization-y'];
                                        } else {
                                            yOffset = Math.max(+p.component.custom['personalization-y'], yOffset);
                                        }
                                    }

                                    if (p.component.custom['personalization-color'] != null) {
                                        fontColor = p.component.custom['personalization-color'];
                                    }

                                    if (p.component.custom['personalization-scale'] != null) {
                                        fontScale = p.component.custom['personalization-scale'];
                                    }

                                    const font = p.component.description.find((d) => d.definition === constants.DEFINITION_FONT);
                                    if (font) {
                                        fontFamily = font.code;
                                    }
                                });

                                if (yOffset == null) {
                                    yOffset = (((top + bottom) * 0.5) - (height * 0.5));
                                } else {
                                    yOffset += this.viewController.blueprint.custom['v-group-y-offset']
                                        ? (+this.viewController.blueprint.custom['v-group-y-offset'])
                                        : 100;
                                }

                                const group = this.viewController.blueprint.custom[`v-group-${view.custom['v-design-area']}`];

                                this.$store.dispatch('setGroup', { placement: placement.code, group });

                                const newSettings = {
                                    'v-design-on': view.custom['v-design-area'],
                                    mask: view.custom['v-design-mask'],
                                    'personalization-x': ((left + right) * 0.5) - (width * 0.5),
                                    'personalization-y': yOffset,
                                    'personalization-rotate': 0,
                                    'personalization-scale': 1,
                                    'personalization-filled': 'true',
                                    'personalization-width': '1',
                                    'personalization-height': '1',
                                    'personalization-size-fit-min': 'none',
                                };

                                if (fontScale) {
                                    newSettings['personalization-scale'] = fontScale;
                                }

                                if (fontColor) {
                                    newSettings['personalization-color'] = fontColor;
                                }

                                if (fontFamily) {
                                    this.viewController.updateSimilarComponent(placement.code, constants.DEFINITION_FONT, fontFamily);
                                }

                                this.viewController.commitCustomAttributes(placement.code, newSettings, {
                                    propagate: true,
                                });
                            }
                        }
                    }

                    // Add lines to secondary bottles
                    if (this.secondaryBottles.length > 0) {
                        const newSecondaryBottles = this.secondaryBottles.map((secondaryBottle) => {
                            const newBottle = secondaryBottle;
                            secondaryBottle.push('');
                            return newBottle;
                        });

                        this.$store.dispatch('setSecondaryBottles', newSecondaryBottles);
                    }

                    this.beginEdit(placement, this.viewController.selectedAtPlacement(placement.code));
                }
            },

            /**
             * Removes text from bottles.
             */
            removeBottlesText(placement) {
                const index = this.filledTextPlacements.findIndex((p) => p.placement.code === placement.placement.code);

                if (this.secondaryBottles.length > 0 && index >= 0) {
                    const newSecondaryBottles = this.secondaryBottles.map((secondaryBottle) => {
                        if (index < secondaryBottle.length) {
                            secondaryBottle.splice(index, 1);
                        }

                        return secondaryBottle;
                    });

                    this.$store.dispatch('setSecondaryBottles', newSecondaryBottles);
                }
            },

            /**
             * Removes a text.
             */
            listRemoveText(text) {
                this.removeBottlesText(text);

                this.$store.dispatch('clearPlacement', text);

                if (this.filledTextPlacements.length === 0) {
                    this.accept();
                }
            },

            /**
             * Edits an text.
             */
            listEditText(text) {
                this.beginEdit(text.placement, text.component);
            },

            /**
             * Edit text color on small screens.
             */
            editSmallColor() {
                this.revertColor = this.selectedColor;

                this.editMode = 'color';
            },

            /**
             * Edit text color.
             */
            editColor() {
                this.revertColor = this.selectedColor;

                this.mode = 'color';
            },

            /**
             * Edit text font.
             */
            editFont() {
                this.editMode = 'font';
            },

            /**
             * Edit text layering.
             */
            editLayering() {
                this.editMode = 'layering';
            },

            /**
             * Edit text alignment.
             */
            editAlignment() {
                this.editMode = 'alignment';
            },

            /**
             * Back to editing menu.
             */
            backToEdit() {
                this.editMode = null;
            },

            /**
             * Build font style object for the font selector.
             */
            getFontStyle(option) {
                const css = {};

                if (option.custom && option.custom['v-webfont-family']) {
                    css.fontFamily = option.custom['v-webfont-family'];
                }

                return css;
            },

            /**
             * Begin selecting a color.
             */
            selectColor() {
                this.revertColor = this.selectedColor;

                this.mode = 'color';
            },

            /**
             * Get color style.
             */
            getColorStyle(option) {
                const css = {};

                if (option.custom && option.custom.hex) {
                    const { hex } = option.custom;

                    css.backgroundColor = hex.startsWith('#') ? hex : `#${hex}`;
                }

                return css;
            },

            /**
             * Update the color selection.
             */
            pickColor(color) {
                this.viewController.commitCustomAttributes(this.editPlacement.code, {
                    'personalization-color': color.custom.hex,
                }, {
                    propagate: true,
                });
            },

            /**
             * Accepts color selection.
             */
            acceptColor() {
                this.mode = 'edit';
            },

            /**
             * Revert color selection.
             */
            cancelColor() {
                if (this.revertColor) {
                    this.viewController.commitCustomAttributes(this.editPlacement.code, {
                        'personalization-color': this.revertColor.custom.hex,
                    }, {
                        propagate: true,
                    });
                }

                this.mode = 'edit';
            },

            /**
             * Removes current text block.
             */
            removeText() {
                this.removeBottlesText({
                    placement: this.editPlacement,
                });

                this.$store.dispatch('clearPlacement', {
                    placement: this.editPlacement,
                });

                if (this.filledTextPlacements.length === 0) {
                    this.accept();
                } else {
                    this.mode = 'list';

                    this.$store.dispatch('lockSwipe', false);
                    this.$store.dispatch('editPlacement', null);
                    this.editPlacement = null;
                }
            },

            /**
             * Moves the current design to another surface.
             */
            moveDesign() {
                if (this.editPlacement && this.editComponent && this.currentView) {
                    const viewDesignArea = this.currentView.view.custom['v-design-area'];
                    const designOn = this.editComponent.custom['v-design-on'];

                    if (viewDesignArea !== designOn) {
                        this.$store.dispatch('renderOnlyOnView', {
                            view: this.currentView,
                        });

                        const {
                            left, right, top, bottom,
                        } = designArea(this.$store.state.customizer, viewDesignArea);

                        const width = +this.editComponent.custom['personalization-width'];
                        const height = +this.editComponent.custom['personalization-height'];

                        if (left != null && right != null && top != null && bottom != null) {
                            const group = this.viewController.blueprint.custom[`v-group-${viewDesignArea}`];

                            this.$store.dispatch('setGroup', { placement: this.editPlacement.code, group });

                            this.viewController.commitCustomAttributes(this.editPlacement.code, {
                                'v-design-on': viewDesignArea,
                                mask: this.currentView.view.custom['v-design-mask'],
                                'personalization-x': ((left + right) * 0.5) - (width * 0.5),
                                'personalization-y': ((top + bottom) * 0.5) - (height * 0.5),
                            }, {
                                propagate: true,
                            });
                        }
                    }
                }
            },
        },
    };
</script>
